import React, { useState, useRef } from 'react';

const ImageUpload = ({ 
  type = 'avatar', // 'avatar' or 'selfie'
  onImageSelect, 
  currentImage = null,
  maxSize = 1, // in MB
  multiple = false,
  disabled = false 
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImage);
  const fileInputRef = useRef(null);

  const maxSizeBytes = maxSize * 1024 * 1024;

  const validateFile = (file) => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      throw new Error('Please select an image file');
    }

    // Check file size
    if (file.size > maxSizeBytes) {
      throw new Error(`File size must be less than ${maxSize}MB`);
    }

    return true;
  };

  const processFile = (file) => {
    try {
      validateFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result.split(',')[1]; // Remove data:image/...;base64, prefix
        const imageData = {
          file,
          base64: base64Data,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: e.target.result
        };
        
        setPreviewUrl(e.target.result);
        onImageSelect(imageData);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      processFile(files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (disabled) return;

    const files = e.target.files;
    if (files && files[0]) {
      processFile(files[0]);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onImageSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const typeLabel = type === 'avatar' ? 'Avatar' : 'Selfie';
  const description = type === 'avatar' 
    ? `Upload a profile picture for your character (max ${maxSize}MB)`
    : `Upload selfie images for your character to use (max ${maxSize}MB each)`;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-300">
        {typeLabel} {type === 'avatar' ? '(Optional)' : 'Images (Optional)'}
      </label>
      <p className="text-xs text-gray-400">{description}</p>
      
      <div
        className={`relative border-2 border-dashed rounded-lg p-4 transition-all cursor-pointer
          ${dragActive 
            ? 'border-blue-400 bg-blue-50 bg-opacity-10' 
            : 'border-gray-600 hover:border-gray-500'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleChange}
          className="hidden"
          disabled={disabled}
        />
        
        {previewUrl ? (
          <div className="relative">
            <img
              src={previewUrl}
              alt={`${typeLabel} preview`}
              className="w-full h-32 object-cover rounded"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm"
              disabled={disabled}
            >
              ×
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="text-gray-400 mb-2">
              <svg className="mx-auto h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-sm text-gray-400">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PNG, JPG, GIF up to {maxSize}MB
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;